import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clapperboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AudienceUseCasesStrip } from '@/components/audience/AudienceUseCasesStrip';
import { CreationFlowsShowcase } from '@/components/creationFlows/CreationFlowsShowcase';
import { storyFlowNavigationState } from '@/data/storyFlows';

export const StoryFlowPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('storycraft_user') || '{}');

  const [interestOpen, setInterestOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [interestEmail, setInterestEmail] = useState(user.email || '');
  const [interestPhone, setInterestPhone] = useState(user.phone || '');
  const [interestRequirements, setInterestRequirements] = useState('');
  const [submittingInterest, setSubmittingInterest] = useState(false);

  const startFlow = (flow) => {
    if (flow.available && flow.appRoute) {
      const storyFlow = storyFlowNavigationState(flow);
      const navState = { ...(flow.navState || {}), ...(storyFlow ? { storyFlow } : {}) };
      navigate(flow.appRoute, Object.keys(navState).length ? { state: navState } : undefined);
      return;
    }
    setSelectedFlow(flow);
    setInterestEmail((prev) => prev || user.email || '');
    setInterestPhone((prev) => prev || user.phone || '');
    setInterestRequirements('');
    setInterestOpen(true);
  };

  const submitInterest = async (e) => {
    e.preventDefault();
    if (!selectedFlow) return;
    const email = interestEmail.trim();
    const requirements = interestRequirements.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (requirements.length < 10) {
      toast.error('Please describe your requirements (at least 10 characters)');
      return;
    }
    setSubmittingInterest(true);
    try {
      await api.post('/flow-interest', {
        flow_id: selectedFlow.id,
        flow_title: selectedFlow.title,
        email,
        phone: interestPhone.trim() || null,
        requirements,
      });
      toast.success('Request received — we will notify you when this flow launches.');
      setInterestOpen(false);
      setSelectedFlow(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmittingInterest(false);
    }
  };

  return (
    <div className="max-w-[88rem] mx-auto px-4 sm:px-5 lg:px-6 py-4 sm:py-5" data-testid="story-flow-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 28 }}
        className="space-y-4"
      >
        <div
          className="dashboard-panel-hero overflow-hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5 pl-5 sm:pl-6"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--primary) / 0.1) 0%, hsl(260 40% 12%) 55%, hsl(220 30% 8%) 100%)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-8 h-8 flex items-center justify-center shrink-0 border border-violet-500/25 bg-violet-500/10"
            >
              <Clapperboard className="w-4 h-4 text-violet-500" />
            </div>
            <div className="min-w-0">
              <p className="dashboard-section-label mb-0.5">AI Movie Creator</p>
              <h1
                className="font-heading font-bold text-lg sm:text-xl tracking-tight"
                style={{ color: 'hsl(var(--foreground))' }}
              >
                Choose your <span className="gradient-text">creation flow</span>
              </h1>
              <p className="text-[11px] mt-0.5 truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Narration, learning & dialogue live · request access for upcoming formats
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="dashboard-btn rounded-sm h-8 px-3 shrink-0"
            style={{ borderColor: 'var(--glass-border)', color: 'hsl(var(--foreground))' }}
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
            Dashboard
          </Button>
        </div>

        <CreationFlowsShowcase
          variant="app"
          size="roomy"
          showHeader={false}
          testIdPrefix="story-flow"
          onFlowClick={startFlow}
          data-testid="story-flow-grid"
        />

        <div className="dashboard-panel px-4 py-4 sm:px-5 sm:py-5">
          <p className="dashboard-section-label mb-3">Who Twinnee is for</p>
          <AudienceUseCasesStrip />
        </div>
      </motion.div>

      <Dialog open={interestOpen} onOpenChange={setInterestOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="flow-interest-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              {selectedFlow?.title || 'Flow'} — coming soon
            </DialogTitle>
            <DialogDescription className="text-sm">
              Share your email and what you want to build. We will notify you when this flow is ready.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInterest} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="flow-interest-email" className="text-xs">Email</Label>
              <Input
                id="flow-interest-email"
                type="email"
                required
                value={interestEmail}
                onChange={(e) => setInterestEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-9"
                data-testid="flow-interest-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="flow-interest-phone" className="text-xs">
                Phone <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="flow-interest-phone"
                type="tel"
                value={interestPhone}
                onChange={(e) => setInterestPhone(e.target.value)}
                placeholder="+1 555 000 0000"
                className="h-9"
                data-testid="flow-interest-phone"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="flow-interest-requirements" className="text-xs">Your requirements</Label>
              <Textarea
                id="flow-interest-requirements"
                required
                rows={3}
                value={interestRequirements}
                onChange={(e) => setInterestRequirements(e.target.value)}
                placeholder="Video type, audience, length, style, integrations…"
                data-testid="flow-interest-requirements"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setInterestOpen(false)}
                disabled={submittingInterest}
                className="dashboard-btn rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submittingInterest}
                data-testid="flow-interest-submit"
                className="dashboard-btn rounded-sm"
                style={{ background: 'var(--btn-primary-bg)', color: 'white' }}
              >
                {submittingInterest ? 'Sending…' : 'Notify me'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
